/**
 * AtlasAutoRunModule — the "Auto-run" chip and the cancel notices (sweep 2026-10-05). The behaviour lives in
 * AtlasInputModule (`toggleAutoRun`, the `autoRun` key / pad action) and lib/auto_run.ts (the rules); this module only
 * SHOWS it: a chip while it is on, a toast when something other than a key ended it (the water, a mount, a cinematic).
 * THE TOKEN LAW: every colour is a `--ax-*` token. Lever `__autoRun()`.
 */
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { keyHint } from '../lib/keybinds.js';
import { cancelNotice } from '../lib/auto_run.js';
import type { AtlasInputModule } from './AtlasInputModule.js';

export class AtlasAutoRunModule implements IModule {
  readonly name = 'AtlasAutoRun';
  private chip: HTMLDivElement | null = null;
  private input: AtlasInputModule | null = null;
  private seenSeq = 0;
  private toasts = 0;

  init(app: App): void {
    const c = document.createElement('div');
    c.id = 'autorun-chip';
    c.className = 'ax-chip';
    // under the windows (40+), over the world; pointer-transparent
    c.style.cssText = 'position:fixed;left:50%;top:132px;transform:translateX(-50%);z-index:31;display:none;pointer-events:none;'
      + 'font:600 12px var(--ax-font);color:var(--ax-accent);background:color-mix(in srgb, var(--ax-bg) 82%, transparent);'
      + 'border:1px solid var(--ax-line2);border-radius:999px;padding:3px 12px';
    c.setAttribute('role', 'status');
    document.body.appendChild(c);
    this.chip = c;
    app.world.addSystem(makeSystem('AutoRunChip', Phase.UI, 36, () => this.tick(app)));
    (window as unknown as Record<string, unknown>).__autoRun = () => {
      const i = this.input?.autoRunInfo ?? { on: false, reason: '', seq: 0 };
      return { ...i, chipShown: !!this.chip && this.chip.style.display !== 'none', chipText: this.chip?.textContent ?? '', toasts: this.toasts, toggle: () => this.input?.toggleAutoRun() ?? false, intent: { x: atlas.intentX, z: atlas.intentZ } };
    };
  }

  private tick(app: App): void {
    this.input ??= (app.getModule('AtlasInput') as AtlasInputModule | undefined) ?? null;
    const info = this.input?.autoRunInfo;
    if (!info || !this.chip) return;
    const text = `Auto-run · ${keyHint('autoRun')} to stop`;
    if (info.on) {
      if (this.chip.style.display !== 'block') this.chip.style.display = 'block';
      if (this.chip.textContent !== text) this.chip.textContent = text;
    } else if (this.chip.style.display !== 'none') this.chip.style.display = 'none';
    if (info.seq !== this.seenSeq) {
      this.seenSeq = info.seq;
      const msg = !info.on ? cancelNotice(info.reason) : '';
      if (msg) { this.toasts++; bus.emit('atlas:toast', { text: msg, kind: 'info', ms: 2200 }); }
    }
  }

  dispose(): void { this.chip?.remove(); this.chip = null; }
}
